import { useNavigate } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import Seo from "../components/Seo";

const HERO_IMG =
  "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/0a/38/74/dd/iconic-waikiki.jpg?w=1400&h=-1&s=1";

/**
 * Landing — minimalist entry page with the iconic Waikiki hero photo as
 * the backdrop, an ornate double-line gold trim frame around it, and just
 * two focused elements in the centre: the HIGHER VISION headline and a
 * single "Join HigherVision" call-to-action.
 */
export default function Landing() {
  const nav = useNavigate();
  const goAuth = () => nav("/login");

  return (
    <>
      <Seo description="Hawaii's social network for opportunities, teams, and community." />
      <div className="relative min-h-screen overflow-hidden flex items-center justify-center px-6"
           data-testid="landing-page">

        {/* Hero photo (Waikiki) with a slow Ken-Burns-style breathe. */}
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover hero-ken-burns"
          style={{ filter: "saturate(1.08) contrast(1.05)" }}
        />

        {/* Dark veil for text legibility — gradient darkens the top and
            bottom slightly more than the middle so the horizon still pops
            through behind the headline. */}
        <div className="absolute inset-0 pointer-events-none"
             aria-hidden="true"
             style={{
               background:
                 "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.30) 45%, rgba(0,0,0,0.75) 100%)," +
                 "radial-gradient(ellipse 60% 45% at 50% 45%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%)",
             }}/>

        {/* Ornate gilded trim frame — double-line inset border to keep the
            Marble & Gold aesthetic consistent with the rest of the app. */}
        <div className="absolute inset-3 sm:inset-5 rounded-[6px] pointer-events-none"
             aria-hidden="true"
             style={{
               border: "2px solid transparent",
               borderImage:
                 "linear-gradient(135deg, #FFE58A 0%, #D4AF37 30%, #6b4d00 55%, #D4AF37 80%, #FFE58A 100%) 1",
               boxShadow:
                 "inset 0 0 0 1px rgba(0,0,0,0.85), 0 0 42px rgba(255,215,0,0.20)",
             }}/>
        <div className="absolute inset-6 sm:inset-9 rounded-[3px] pointer-events-none"
             aria-hidden="true"
             style={{
               border: "1px solid transparent",
               borderImage:
                 "linear-gradient(135deg, rgba(255,215,0,0.65), rgba(107,77,0,0.4)) 1",
             }}/>

        {/* Foreground content */}
        <div className="relative z-10 text-center max-w-3xl">
          <h1 className="font-display leading-[0.92]"
              data-testid="landing-headline"
              style={{
                fontSize: "clamp(3rem, 12vw, 8.5rem)",
                background:
                  "linear-gradient(180deg, #FFF3AE 0%, #FFD700 40%, #D4AF37 70%, #6b4d00 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                filter:
                  "drop-shadow(0 4px 3px rgba(0,0,0,0.75)) drop-shadow(0 0 34px rgba(255,215,0,0.30))",
                letterSpacing: "0.015em",
              }}>
            HIGHER<br/>VISION
          </h1>
          <div className="mt-4 text-[11px] sm:text-xs uppercase tracking-[0.36em]"
               style={{
                 color: "#F2DC8C",
                 textShadow: "0 1px 3px rgba(0,0,0,0.95)",
               }}>
            Hawaii&apos;s Social Network
          </div>

          <button
            onClick={goAuth}
            data-testid="landing-join"
            className="mt-10 inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-display uppercase text-sm tracking-[0.28em] transition-transform active:scale-95 hover:scale-[1.02]"
            style={{
              background:
                "linear-gradient(180deg, #FFF3AE 0%, #FFD700 35%, #D4AF37 65%, #6b4d00 100%)",
              color: "#0a0703",
              border: "1.5px solid #FFD700",
              boxShadow:
                "0 14px 32px rgba(0,0,0,0.65), 0 0 26px rgba(255,215,0,0.45), inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -2px 0 rgba(107,77,0,0.6)",
            }}>
            Join HigherVision
            <ArrowRight size={16} weight="bold"/>
          </button>
        </div>
      </div>
    </>
  );
}
